import React, { useContext, useEffect, useRef, useState } from 'react';
import { AccountContext } from '../account/AccountContext';
import StepProgress from './StepProgress';
import { toFormattedDate } from '@app/utilities';

const POLLING_INTERVAL_MS = 500;
const COMPLETED_DISPLAY_MS = 5000;

const TERMINAL_STATES = ['Completed', 'Failed', 'Cancelled', 'Error'];

function stripPrefix(stepName: string): string {
  return stepName.replace(/^EndOf(Day|Period)\./, '');
}

export default function EodProgress() {
  const { accountId, eodoService, eodDate, isRunningEod } = useContext(AccountContext);
  const [eodProgress, setEodProgress] = useState(undefined as any);
  const [displayDate, setDisplayDate] = useState<Date | undefined>(undefined);
  const inFlightRef = useRef(false);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!accountId || !eodDate) return;

    setEodProgress(undefined);
    setDisplayDate(eodDate);

    const fetchProgress = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        const progress = await eodoService.getEodView(accountId, eodDate);
        if (progress) {
          setEodProgress(progress);
        }
      } catch {
        // Swallow polling errors to avoid disrupting the UI
      } finally {
        inFlightRef.current = false;
      }
    };

    fetchProgress();

    if (isRunningEod) {
      const intervalId = setInterval(fetchProgress, POLLING_INTERVAL_MS);
      return () => clearInterval(intervalId);
    }
  }, [accountId, eodDate, isRunningEod]);

  useEffect(() => {
    if (!isRunningEod && eodProgress?.state) {
      if (TERMINAL_STATES.includes(eodProgress.state)) {
        clearTimerRef.current = setTimeout(() => setEodProgress(undefined), COMPLETED_DISPLAY_MS);
      } else {
        setEodProgress(undefined);
      }
    }
    return () => {
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    };
  }, [isRunningEod, eodProgress?.state]);

  const showPending = isRunningEod && !eodProgress?.state;

  return (
    <>
      {showPending && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className="notched circle loading icon" />
          <span style={{ fontWeight: 'bold' }}>⏳ Starting EOD {displayDate && toFormattedDate(displayDate.toISOString())}...</span>
        </div>
      )}
      {eodProgress?.state && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="ui mini steps">
              {eodProgress?.steps &&
                Object.entries(eodProgress?.steps)
                  .filter(([key]) => key.includes('EndOfDay'))
                  .map(([key, value]: any) => (
                    <StepProgress stepName={stripPrefix(key)} key={key} progressState={value.progressState} />
                  ))}
            </div>
            {eodProgress.state === 'Completed' && (
              <span style={{ color: '#21ba45', fontWeight: 'bold', whiteSpace: 'nowrap' }}>✅ EOD {displayDate && toFormattedDate(displayDate.toISOString())} completed successfully</span>
            )}
            {eodProgress.state === 'Failed' && (
              <span style={{ color: '#db2828', fontWeight: 'bold', whiteSpace: 'nowrap' }}>❌ EOD {displayDate && toFormattedDate(displayDate.toISOString())} failed</span>
            )}
            {!TERMINAL_STATES.includes(eodProgress.state) && (
              <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>⏳ EOD {displayDate && toFormattedDate(displayDate.toISOString())} {eodProgress.state}...</span>
            )}
          </div>
          <div className="ui mini steps" style={{ flexWrap: 'wrap' }}>
            {eodProgress?.steps &&
              Object.entries(eodProgress?.steps)
                .filter(([key]) => key.includes('EndOfPeriod'))
                .map(([key, value]: any) => (
                  <StepProgress stepName={stripPrefix(key)} key={key} progressState={value.progressState} />
                ))}
          </div>
        </>
      )}
    </>
  );
}
